import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { rem } from '../../utils';

const StyledImage = styled.img<{
  $rounded?: boolean;
  $isLoaded: boolean;
}>`
  ${({ $rounded, theme }) => $rounded && `border-radius: ${rem(theme.Border.Radius.Medium)};`}

  ${({ theme, $isLoaded }) => css`
    opacity: ${$isLoaded ? 1 : 0};
    transition: opacity ${theme.Transition.Timing.Out};
  `}
`;

export interface ImageProps {
  source: string | string[];
  sourceFallback?: string;
  alt?: string;
  rounded?: boolean;
  className?: string;
  id?: string;
  draggable?: boolean;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>, source: string) => void;
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  height?: number;
  width?: number;
  lazy?: boolean;
}

export const Image = React.forwardRef(function Image(
  props: ImageProps,
  ref: React.Ref<HTMLImageElement>
): JSX.Element | null {
  const { alt, rounded, onLoad, className, onError } = props;
  const [isLoaded, setIsLoaded] = useState(false);

  const [activeSource, setActiveSource] = useState<number>(0);

  useEffect(() => {
    setActiveSource(0);
  }, [props.source]);

  const sourceList = useMemo(() => {
    const temp = Array.isArray(props.source) ? [...props.source] : [props.source];

    if (props.sourceFallback) {
      temp.push(props.sourceFallback);
    }

    return temp;
  }, [props.source, props.sourceFallback]);

  const source = sourceList[activeSource];

  const handleLoadSuccess = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setIsLoaded(true);
      onLoad?.(event, source);
    },
    [onLoad, source]
  );

  const handleLoadError = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (activeSource < sourceList.length - 1) {
        setActiveSource((prev) => prev + 1);
      } else {
        onError?.(event);
      }
    },
    [activeSource, sourceList, onError]
  );

  if (!source) return null;

  return (
    <StyledImage
      ref={ref}
      id={props.id}
      className={className}
      src={source}
      alt={alt}
      $isLoaded={isLoaded}
      $rounded={rounded}
      draggable={props.draggable}
      onLoad={handleLoadSuccess}
      onError={handleLoadError}
      height={props.height}
      width={props.width || '100%'}
      loading={props.lazy ? 'lazy' : undefined}
      decoding={props.lazy ? 'async' : undefined}
      fetchPriority={props.lazy ? 'low' : undefined}
    />
  );
});
